"use client";

// Builder Page - Main AI management dashboard
// Shows all AIs with types, spec sheets, connect/edit buttons, and AI redesign capability

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import PremiumHeroBackground from "@/components/ui/PremiumHeroBackground";
import BuilderOverview from "@/components/builder/BuilderOverview";
import AIBuilderDashboard from "@/components/builder/AIBuilderDashboard";
import AIEditInterface from "@/components/builder/AIEditInterface";
import dynamicImport from "next/dynamic";
import ErrorBoundary from "@/components/builder/ErrorBoundary";
import type { AITemplate } from "@/lib/templates/templates";
import { getUserAIs } from "@/lib/ai/entityManager";
import { entityToAiProfile } from "@/types/aiEntity";
import { useUser } from "@/lib/user/context";
import { getUserId } from "@/lib/user/identity";

// Dynamically import ChatBuilderInterface to prevent SSR issues
const ChatBuilderInterface = dynamicImport(() => import("@/components/builder/ChatBuilderInterface"), {
  ssr: false,
  loading: () => (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-900">
      <div className="w-8 h-8 border-2 border-white/60 border-t-transparent rounded-full animate-spin" aria-hidden />
      <p className="text-sm text-white/80">Loading builder...</p>
    </div>
  ),
});

// Prevent static generation - uses sessionStorage
export const dynamic = 'force-dynamic';

function BuilderContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useUser();
  const [intent, setIntent] = useState<string | null>(null);
  const [template, setTemplate] = useState<AITemplate | null>(null);
  const [showBuilderOverview, setShowBuilderOverview] = useState(false);
  const [mode, setMode] = useState<string | null>(null);
  const [aiId, setAiId] = useState<string | null>(null);
  const [buildProfile, setBuildProfile] = useState<Partial<import("@/types/ai").AiProfile> | null>(null);

  useEffect(() => {
    // Read URL params
    if (typeof window !== "undefined") {
      const intentParam = searchParams?.get("intent");
      const templateIdParam = searchParams?.get("templateId");
      const modeParam = searchParams?.get("mode");
      const aiIdParam = searchParams?.get("aiId");

      if (intentParam) {
        setIntent(intentParam);
        
        if (templateIdParam) {
          // Load template by ID (try presets first for full-business etc., then old templates)
          Promise.all([
            import("@/lib/templates/presets").then((m) => m.getTemplatePresetById(templateIdParam)),
            import("@/lib/templates/templates").then((m) => m.getTemplateById(templateIdParam)),
          ]).then(([preset, oldTemplate]) => {
            if (preset) setTemplate(preset as any);
            else if (oldTemplate) setTemplate(oldTemplate);
          });
        }
        
        setShowBuilderOverview(true);
      } else if (modeParam === "edit" || modeParam === "connect") {
        // Edit or connect mode - show appropriate interface
        setMode(modeParam);
        setAiId(aiIdParam || null);
      } else if (modeParam === "build" && aiIdParam) {
        // Build mode - show chat builder interface
        setMode("build");
        setAiId(aiIdParam);
        // Load AI profile for builder
        const loadProfile = async () => {
          try {
            const userId = user?.id ?? (typeof window !== "undefined" ? getUserId() : undefined);
            if (userId) {
              const ais = await getUserAIs(userId);
              const ai = ais.find((a) => a.id === aiIdParam);
              if (ai) {
                setBuildProfile(entityToAiProfile(ai));
                return;
              }
            }
            // Fallback: try to load directly from database (client-side only)
            if (typeof window !== "undefined") {
              try {
                const { db: dbInstance } = await import("@/lib/db");
                if (dbInstance && dbInstance.aiProfiles) {
                  const profile = await dbInstance.aiProfiles.get(aiIdParam);
                  if (profile) {
                    setBuildProfile(profile);
                  }
                }
              } catch (dbError) {
                console.error("Failed to load profile from database:", dbError);
              }
            }
          } catch (error) {
            console.error("Failed to load profile:", error);
          }
        };
        loadProfile();
      }
    }
  }, [searchParams, user]);

  // If we have intent from start page, show BuilderOverview
  if (showBuilderOverview && intent) {
    return (
      <PremiumHeroBackground className="min-h-screen">
        <BuilderOverview intent={intent} template={template} />
      </PremiumHeroBackground>
    );
  }

  // If we have build mode, show chat builder interface
  if (mode === "build" && aiId) {
    if (!buildProfile) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-900">
          <div className="w-8 h-8 border-2 border-white/60 border-t-transparent rounded-full animate-spin" aria-hidden />
          <p className="text-sm text-white/80">Loading AI profile...</p>
        </div>
      );
    }
    return (
      <ErrorBoundary>
        <ChatBuilderInterface
          profile={buildProfile}
          onClose={() => router.push("/builder")}
          onProfileUpdate={(updatedProfile) => {
            setBuildProfile((prev) => prev ? { ...prev, ...updatedProfile } : updatedProfile);
          }}
        />
      </ErrorBoundary>
    );
  }

  // If we have edit/connect mode, show edit interface
  if (mode && aiId && mode !== "build") {
    return (
      <AIEditInterface
        aiId={aiId}
        mode={mode as "edit" | "connect"}
        onClose={() => router.push("/builder")}
      />
    );
  }

  // Show AI Builder Dashboard (main view)
  return (
    <ErrorBoundary>
      <AIBuilderDashboard />
    </ErrorBoundary>
  );
}

export default function BuilderPage() {
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="text-white">Loading...</div>
        </div>
      }>
        <BuilderContent />
      </Suspense>
    </ErrorBoundary>
  );
}
